/**
 * locationSync.js — batch upload of ambient presence fixes to the server.
 *
 * Store-and-forward: fixes are written locally by the PresenceTracker and
 * pushed to the server in batches (not per-fix) to conserve battery and data.
 * Unsynced fixes accumulate offline and flush when connectivity returns.
 *
 * Endpoint: POST {BASE_URL}api/location.php
 *   body: { deviceId, fixes: [{ client_uuid, longitude, latitude, accuracy,
 *           altitude, heading, speed, mode, recorded_at }, ...] }
 *   200:  { synced: ["<client_uuid>", ...] }   // uuids the server accepted
 *
 * The client marks the corresponding local rows synced. Because each fix
 * carries a stable client_uuid and the server upserts on it, re-sending a
 * batch is idempotent — safe if a response is lost.
 *
 * @module sync/locationSync
 */

import {
  getUnsyncedLocationFixes,
  markLocationFixesSynced,
  pruneOldLocationFixes,
  getSetting,
  setSetting,
} from '../database.js';

const ENDPOINT = `${import.meta.env.BASE_URL}api/location.php`;
const BATCH_SIZE = 200;
const DEVICE_ID_KEY = 'device_id';

let _inFlight = false;

/** Stable per-install device id (generated + persisted on first use). */
async function _deviceId() {
  let id = await getSetting(DEVICE_ID_KEY);
  if (!id) {
    id = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : 'dev-' + Math.random().toString(16).slice(2) + Date.now().toString(16);
    await setSetting(DEVICE_ID_KEY, id);
  }
  return id;
}

/**
 * Push all pending fixes in batches. Safe to call repeatedly; a second call
 * while one is in flight is a no-op. Silently returns if offline.
 *
 * @returns {Promise<{ pushed:number, failed:number }>}
 */
export async function syncLocationFixes() {
  if (_inFlight) return { pushed: 0, failed: 0 };
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { pushed: 0, failed: 0 };
  }
  _inFlight = true;
  let pushed = 0, failed = 0;
  try {
    const deviceId = await _deviceId();

    // Loop batches until there's nothing left (or a batch fails).
    while (true) {
      const rows = await getUnsyncedLocationFixes(BATCH_SIZE);
      if (!rows.length) break;

      const fixes = rows.map((r) => ({
        client_uuid: r.client_uuid,
        longitude: r.longitude,
        latitude: r.latitude,
        accuracy: r.accuracy,
        altitude: r.altitude,
        heading: r.heading,
        speed: r.speed,
        mode: r.mode,
        recorded_at: r.recorded_at,
      }));

      let resp;
      try {
        resp = await fetch(ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ deviceId, fixes }),
        });
      } catch (netErr) {
        // Offline / network error — stop; we'll retry later.
        failed += rows.length;
        break;
      }

      if (!resp.ok) { failed += rows.length; break; }

      let body;
      try { body = await resp.json(); } catch (_) { body = null; }
      const acceptedUuids = new Set((body && body.synced) || []);

      // Map accepted uuids back to local ids.
      const syncedIds = rows.filter((r) => acceptedUuids.has(r.client_uuid)).map((r) => r.id);
      if (!syncedIds.length) { failed += rows.length; break; }

      await markLocationFixesSynced(syncedIds);
      pushed += syncedIds.length;

      // Fewer accepted than sent, or a short batch → stop to avoid a hot loop.
      if (syncedIds.length < rows.length || rows.length < BATCH_SIZE) break;
    }

    // Opportunistic housekeeping.
    if (pushed) { try { await pruneOldLocationFixes(30); } catch (_) {} }
  } finally {
    _inFlight = false;
  }
  return { pushed, failed };
}

export default { syncLocationFixes };
